import * as React from 'react';
import {
  AddressAutocompleteInput,
  type AddressAutocompleteInputProps,
  type AddressDetailsResolver,
  type AddressManualFieldsContext,
  type AddressSuggestion,
  type AddressSuggestionsProvider,
  type AddressValue,
} from './address-autocomplete-input';
import { getOrderedCountryCodes } from './country-select';
import {
  frenchAddressDetailsResolver,
  frenchAddressSuggestionsProvider,
} from './french-address-input';
import {
  FRENCH_DEPARTMENT_OPTIONS,
  type FrenchDepartmentCode,
} from './french-departments';
import { Label } from './label';
import { Select } from './select';
import {
  createSwissAddressDetailsResolver,
  createSwissAddressSuggestionsProvider,
  type GeoAdminCountry,
} from './swiss-address-input';
import { SWISS_CANTONS, type SwissCanton } from './swiss-cantons';

export type AddressInputProps = Pick<
  AddressAutocompleteInputProps<AddressValue, AddressSuggestion<AddressValue>>,
  | 'value'
  | 'onChange'
  | 'labels'
  | 'placeholders'
  | 'errors'
  | 'helperText'
  | 'disabled'
  | 'required'
  | 'requiredFields'
  | 'allowedCountries'
  | 'locale'
  | 'name'
  | 'id'
  | 'searchId'
  | 'className'
  | 'onFetchError'
  | 'disablePortal'
  | 'defaultManual'
  | 'forceManual'
> & {
  /** Restrict Swiss suggestions and the manual canton selector. */
  allowedCantons?: readonly SwissCanton[];
  /** Hide the manual canton selector while retaining resolved canton values. */
  showCanton?: boolean;
};

function RegionField({
  country,
  context,
  allowedCantons,
  showCanton,
}: {
  country: string;
  context: AddressManualFieldsContext<AddressValue>;
  allowedCantons?: readonly SwissCanton[];
  showCanton: boolean;
}) {
  const {
    value,
    disabled,
    isRequired,
    disablePortal,
    baseId,
    name,
    errors,
    labels,
    placeholders,
    updateValue,
  } = context;

  if (country === 'CH' && showCanton) {
    return (
      <div className="grid gap-1.5">
        <Label htmlFor={`${baseId}-canton`} required={isRequired('canton')}>
          {labels.canton}
        </Label>
        <Select
          id={`${baseId}-canton`}
          options={(allowedCantons ?? SWISS_CANTONS).map((canton) => ({
            value: canton,
            label: canton,
          }))}
          value={value.canton}
          onChange={(canton) => updateValue({ canton: canton as SwissCanton })}
          placeholder={placeholders.canton}
          disabled={disabled}
          error={Boolean(errors.canton)}
          disablePortal={disablePortal}
        />
        {name && (
          <input
            type="hidden"
            name={`${name}.canton`}
            value={value.canton ?? ''}
            disabled={disabled}
          />
        )}
        {typeof errors.canton === 'string' && (
          <p className="text-sm text-destructive">{errors.canton}</p>
        )}
      </div>
    );
  }

  if (country === 'FR') {
    return (
      <div className="grid gap-1.5">
        <Label
          htmlFor={`${baseId}-department`}
          required={isRequired('department')}
        >
          {labels.department}
        </Label>
        <Select
          id={`${baseId}-department`}
          options={[...FRENCH_DEPARTMENT_OPTIONS]}
          value={value.department}
          onChange={(department) =>
            updateValue({ department: department as FrenchDepartmentCode })
          }
          placeholder={placeholders.department}
          disabled={disabled}
          error={Boolean(errors.department)}
          disablePortal={disablePortal}
          searchable
        />
        {name && (
          <input
            type="hidden"
            name={`${name}.department`}
            value={value.department ?? ''}
            disabled={disabled}
          />
        )}
        {typeof errors.department === 'string' && (
          <p className="text-sm text-destructive">{errors.department}</p>
        )}
      </div>
    );
  }

  return null;
}

const AddressInput = React.forwardRef<HTMLInputElement, AddressInputProps>(
  (
    {
      value,
      onChange,
      allowedCountries,
      allowedCantons,
      showCanton = true,
      locale = 'en',
      ...props
    },
    ref,
  ) => {
    const defaultCountry = React.useMemo(
      () => getOrderedCountryCodes(locale, allowedCountries)[0] ?? 'CH',
      [allowedCountries, locale],
    );
    const country = value?.country ?? defaultCountry;
    const geoAdminCountry: GeoAdminCountry = country === 'LI' ? 'LI' : 'CH';
    const swissProvider = React.useMemo(
      () =>
        createSwissAddressSuggestionsProvider(allowedCantons, geoAdminCountry),
      [allowedCantons, geoAdminCountry],
    );
    const swissResolver = React.useMemo(
      () => createSwissAddressDetailsResolver(allowedCantons, geoAdminCountry),
      [allowedCantons, geoAdminCountry],
    );
    const unifiedSwissResolver = React.useMemo<
      AddressDetailsResolver<AddressSuggestion<AddressValue>, AddressValue>
    >(
      () => async (suggestion, context) => {
        const resolved = await (
          swissResolver as AddressDetailsResolver<
            AddressSuggestion<AddressValue>,
            AddressValue
          >
        )(suggestion, context);
        return resolved;
      },
      [swissResolver],
    );

    let suggestionsProvider:
      AddressSuggestionsProvider<AddressSuggestion<AddressValue>> | undefined;
    let detailsResolver:
      | AddressDetailsResolver<AddressSuggestion<AddressValue>, AddressValue>
      | undefined;
    let debounceMs = 300;

    if (country === 'CH' || country === 'LI') {
      suggestionsProvider = swissProvider as AddressSuggestionsProvider<
        AddressSuggestion<AddressValue>
      >;
      detailsResolver = unifiedSwissResolver;
      debounceMs = 500;
    } else if (country === 'FR') {
      suggestionsProvider = frenchAddressSuggestionsProvider;
      detailsResolver = frenchAddressDetailsResolver;
      debounceMs = 250;
    }

    return (
      <AddressAutocompleteInput
        ref={ref}
        {...props}
        value={value}
        onChange={onChange}
        suggestionsProvider={suggestionsProvider}
        detailsResolver={detailsResolver}
        debounceMs={debounceMs}
        minimumQueryLength={3}
        allowedCountries={allowedCountries}
        defaultCountry={defaultCountry}
        autocompleteCountries={['CH', 'LI', 'FR']}
        locale={locale}
        renderRegionField={(context) => (
          <RegionField
            country={country}
            context={context}
            allowedCantons={allowedCantons}
            showCanton={showCanton}
          />
        )}
      />
    );
  },
);

AddressInput.displayName = 'AddressInput';

export { AddressInput };
export type {
  AddressInputField,
  AddressInputFieldErrors,
  AddressInputLabels,
  AddressInputPlaceholders,
  AddressInputRequiredFields,
  AddressValue,
} from './address-autocomplete-input';
